// Copyright 2022 Sorama B.V.

import { bindable, customElement, inject, observable } from "aurelia-framework";
import { SpecialMeasurementType } from "datatypes/SoramaWebApi";

//the class that contains the root need to implement these methods
export interface IFileTreeContainer {
    import(file: NodeModel): Promise<void>;
    delete(file: NodeModel): Promise<void>;
    download(file: NodeModel): Promise<Blob>;
    onNodeCheckChange(node: NodeModel, isChecked: boolean): void;
    onNodeFocusChange(node: NodeModel, isFocused: boolean): void;
}

export class NodeModel {
    constructor(
        public path: string,
        public isRoot: boolean = false,
        public createDate?: Date
    ) {}
    public container: IFileTreeContainer;

    public children: NodeModel[] = [];
    public isDisabled = false;
    public isFavorite = false;
    public measurementType: string;

    @observable
    public isChecked = false;

    @observable
    public isFocused = false;

    public isCheckedChanged(newValue: boolean, oldValue: boolean): void {
        if (this.container?.onNodeCheckChange) {
            this.container.onNodeCheckChange(this, newValue);
        }
    }

    public isFocusedChanged(newValue: boolean, oldValue: boolean): void {
        if (this.container?.onNodeFocusChange) {
            this.container.onNodeFocusChange(this, newValue);
        }
    }

    public get isFile(): boolean {
        return this.children.length == 0;
    }

    public get selectedFiles(): NodeModel[] {
        return this.subTreeAsList.filter((file: NodeModel) => file.children.length == 0 && file.isChecked == true);
    }

    public get nameAndExtension(): string {
        return this.path.split("/").pop();
    }
    public get name(): string {
        if (!this.isFile) return this.nameAndExtension;
        return this.nameAndExtension.split(".")[0];
    }
    public get extension(): string {
        if (!this.isFile) return "";
        const parts = this.nameAndExtension.split(".");
        if (parts.length > 1 && parts[1]) {
            return parts[1];
        }
        return "";
    }

    public get subTreeAsList(): NodeModel[] {
        const self: NodeModel = this;
        const result = [this as NodeModel].concat(...this.children.map((c) => c.subTreeAsList));
        return result;
    }

    public get childFolders(): NodeModel[] {
        return this.children.filter((x) => !x.isFile);
    }

    public get childFiles(): NodeModel[] {
        return this.children.filter((x) => x.isFile);
    }

    public get type(): string {
        switch (this.extension.toLowerCase()) {
            case "jpg":
            case "png":
            case "jpeg":
                return "Image";
            case "sor":
                return "Measurement";
            case "sorx":
                return "SorX";
            case "json":
                return this.specialMeasurementType(this);
            default:
                return "";
        }
    }

    public get canBeDownloaded(): boolean {
        if (this.isFile) return true;
        return false;
    }

    public get canBeImported(): boolean {
        if (this.isFile && this.type !== "SorX") return true;
        return false;
    }

    public get canBeDeleted(): boolean {
        if (this.isFile && this.type !== "SorX") return true;
        return false;
    }

    private specialMeasurementType(model: NodeModel): string {
        // Note: make sure the special measurement name with specific format, such as RT30-result.json
        const keywords = Object.keys(SpecialMeasurementType);
        const keyword = keywords.find((keyword) => model.name.toUpperCase().includes(keyword));
        if (keyword) {
            return SpecialMeasurementType[keyword];
        }
    }
}

@inject(Element)
@customElement("tree-node")
export class TreeNode {
    @bindable
    public showRowButtons = false;

    @bindable
    public model: NodeModel;

    @bindable
    public showType: boolean;

    @bindable
    public isOpen = true;

    public get icon(): string {
        switch (this.model.extension) {
            case "jpg":
            case "jpeg":
                return "fas fa-image";
            case "mp4":
                return "fas fa-video";
            case "sor":
                return "fas fa-video";
            default:
                return "fas fa-file";
        }
    }

    public get type(): string {
        return this.model.measurementType || this.model.type;
    }

    public toggleOpenState() {
        this.isOpen = !this.isOpen;
    }

    constructor(private element: Element) {}
    private isDownloading = false;
    private isDeleting = false;
    private isImporting = false;

    private async download(): Promise<Blob> {
        this.isDownloading = true;
        return this.model.container.download(this.model).finally(() => (this.isDownloading = false));
    }

    private async import(): Promise<void> {
        this.isImporting = true;
        return this.model.container.import(this.model).finally(() => (this.isImporting = false));
    }

    private async delete(): Promise<void> {
        this.isDeleting = true;
        return this.model.container.delete(this.model).finally(() => (this.isDeleting = false));
    }

    protected clicked(): void {
        // Only focus when leaf node
        if (!this.model.isFile) return;
        this.model.isFocused = !this.model.isFocused;

        // Also toggle the checked state
        this.model.isChecked = !this.model.isChecked;
    }
}
